import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import Bauchbinde from "../components/Bauchbinde";

function flattenTops(nodes, tiefe = 0, prefix = "") {
  let out = [];
  (nodes || []).slice()
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .forEach((node, idx) => {
      const nummer = prefix ? prefix + "." + (idx + 1) : String(idx + 1);
      out.push({ top: node, tiefe, nummer });
      if (node.children && node.children.length)
        out = out.concat(flattenTops(node.children, tiefe + 1, nummer));
    });
  return out;
}

let pdfjsReady = null;
function loadPdfJs() {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfjsReady) return pdfjsReady;
  pdfjsReady = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    s.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
      resolve(window.pdfjsLib);
    };
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return pdfjsReady;
}

export default function ProjektorAnsicht5() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [daten, setDaten] = useState(null);

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const pdfDocRef = useRef(null);
  const lastUrlRef = useRef(null);
  const renderTaskRef = useRef(null);

  const load = () => api.getProjektorDaten(id, 5, token).then(setDaten);

  useEffect(() => {
    load();
    const interval = setInterval(load, 3000);
    return () => clearInterval(interval);
  }, [id, token]);

  const typ = daten?.projektor?.typ;
  const pdfUrl = daten?.pdf_url;

  useEffect(() => {
    if (typ !== "dokument" || !pdfUrl) {
      pdfDocRef.current = null;
      lastUrlRef.current = null;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const pdfjs = await loadPdfJs();
        if (pdfUrl !== lastUrlRef.current) {
          lastUrlRef.current = pdfUrl;
          pdfDocRef.current = null;
          const token = localStorage.getItem("internal_token") || "";
          const res = await fetch(pdfUrl, { headers: { Authorization: "Bearer " + token } });
          if (!res.ok) { console.error("Dokument Status:", res.status); return; }
          const buf = await res.arrayBuffer();
          if (cancelled) return;
          pdfDocRef.current = await pdfjs.getDocument({ data: buf }).promise;
          api.setProjektorPdf(id, { pdf_seiten_gesamt: pdfDocRef.current.numPages }, 5).catch(() => {});
        }
        if (cancelled || !pdfDocRef.current) return;
        const doc = pdfDocRef.current;
        const seite = Math.min(Math.max(1, daten.pdf_seite || 1), doc.numPages);
        const page = await doc.getPage(seite);
        const container = containerRef.current;
        if (!container) return;
        const baseViewport = page.getViewport({ scale: 1 });
        const fitScale = (container.clientWidth - 40) / baseViewport.width;
        const scale = fitScale * (daten.pdf_zoom || 1.0);
        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        if (renderTaskRef.current) { try { renderTaskRef.current.cancel(); } catch {} }
        renderTaskRef.current = page.render({ canvasContext: ctx, viewport });
        try { await renderTaskRef.current.promise; }
        catch (e) { if (e?.name !== "RenderingCancelledException") console.error(e); }
        if (container && canvas) {
          const pct = (daten.pdf_scroll_pct || 0) / 100;
          const max = Math.max(0, canvas.height - container.clientHeight);
          container.scrollTop = max * pct;
        }
      } catch (e) {
        console.error("PDF render error:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [typ, pdfUrl, daten?.pdf_seite, daten?.pdf_zoom, daten?.pdf_scroll_pct, daten?.pdf_updated_at, id]);

  if (!daten) return (
    <div style={{ background: "#0d1b2a", minHeight: "100vh", display: "flex",
                  alignItems: "center", justifyContent: "center", color: "white", fontSize: 24 }}>Lädt…</div>
  );

  const bb = <Bauchbinde text={daten.bauchbinde_text} sichtbar={daten.bauchbinde_sichtbar} />;

  // Dokument-Modus
  if (typ === "dokument") {
    return (
      <div style={{ background: "#0d1b2a", minHeight: "100vh", color: "white",
                    fontFamily: "Arial, sans-serif", padding: 40 }}>
        {pdfUrl ? (
          <div style={{ height: "calc(100vh - 80px)", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "8px 16px", color: "#aaa", fontSize: 14,
                          display: "flex", justifyContent: "space-between" }}>
              <span>📄 Dokument (Reserve)</span>
              <span>Seite {daten.pdf_seite || 1} / {daten.pdf_seiten_gesamt || "?"} · {Math.round((daten.pdf_zoom || 1) * 100)}%</span>
            </div>
            <div ref={containerRef} style={{
              flex: 1, overflow: "auto", background: "#1a2a3a", padding: 20,
              display: "flex", justifyContent: "center", alignItems: "flex-start"
            }}>
              <canvas ref={canvasRef} style={{ background: "white", boxShadow: "0 8px 32px rgba(0,0,0,.5)" }} />
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center",
                        height: "80vh", flexDirection: "column", gap: 16 }}>
            <div style={{ fontSize: 60 }}>📄</div>
            <div style={{ fontSize: 24, color: "#aaa" }}>{daten.sitzung.titel}</div>
            <div style={{ fontSize: 16, color: "#666" }}>Kein Dokument ausgewählt</div>
          </div>
        )}
        {bb}
      </div>
    );
  }

  // TOP-Modus (Default)
  const zeilen = flattenTops(daten.tops).filter(({ top }) => top.oeffentlich !== false);
  return (
    <div style={{ background: "#0d1b2a", minHeight: "100vh", color: "white",
                  fontFamily: "Arial, sans-serif", padding: 40 }}>
      <div style={{ fontSize: 18, color: "#8aa", marginBottom: 8 }}>{daten.sitzung.titel}</div>
      {daten.aktiver_top && (
        <div style={{
          background: "rgba(204,0,0,0.2)", border: "2px solid #cc0000",
          borderRadius: 8, padding: "16px 24px", marginBottom: 24, fontSize: 28, fontWeight: 700
        }}>
          {(flattenTops(daten.tops).find(x => x.top.id === daten.aktiver_top.id)?.nummer
            || daten.aktiver_top.position)}. {daten.aktiver_top.titel}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {zeilen.map(({ top, tiefe, nummer }) => (
          <div key={top.id} style={{
            background: daten.aktiver_top?.id === top.id ? "rgba(204,0,0,0.3)" : "rgba(255,255,255,0.05)",
            border: daten.aktiver_top?.id === top.id ? "2px solid #cc0000" : "2px solid transparent",
            borderRadius: 8, padding: "12px 16px", marginLeft: tiefe * 24,
            display: "flex", gap: 12, alignItems: "center"
          }}>
            <div style={{ width: tiefe > 0 ? 30 : 36, height: tiefe > 0 ? 30 : 36, borderRadius: "50%",
              background: daten.aktiver_top?.id === top.id ? "#cc0000" : "rgba(255,255,255,0.1)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 700, fontSize: tiefe > 0 ? 11 : 14, flexShrink: 0 }}>
              {nummer}
            </div>
            <div style={{ fontSize: tiefe > 0 ? 13 : 15, fontWeight: daten.aktiver_top?.id === top.id ? 700 : 400 }}>
              {top.titel}
            </div>
          </div>
        ))}
      </div>
      {bb}
    </div>
  );
}
